import { Link, createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { LuImport, LuPlus } from "react-icons/lu";

import {
  ButtonStyle,
  PageName,
  PageNumberController,
  PageSizeSelector,
  SensorTable,
  TypographyStyle,
} from "#components";
import { useListedSensors, useTranslate } from "#services";
import { css } from "#styled-system/css";

export const Route = createFileRoute("/_authenticated/facilities/$facilityKey/sensors/")({
  component: Component,
});

function Component() {
  const { facilityKey } = Route.useParams();

  const translate = useTranslate();
  const [pageNumber, setPageNumber] = useState(0);
  const [pageSize, setPageSize] = useState(25);

  const { fetchNextPage, loading, pages } = useListedSensors({ facilityKey }, { pageSize });

  return (
    <main
      className={css({
        display: "flex",
        flexDirection: "column",
        height: "100%",
        padding: { smDown: "16px", sm: "24px 32px" },
        width: "100%",
      })}
      data-maximized
    >
      <PageName>{translate("%SensorManagement.0%")}</PageName>
      <p className={css({ fontSize: "24px", fontWeight: 700 })}>
        {translate("%SensorManagement.0%")}
      </p>

      <div
        className={css({
          alignItems: "flex-end",
          display: "flex",
          justifyContent: "space-between",
        })}
      >
        <div></div>
        <div
          className={css({
            display: "flex",
            gap: "16px",
            justifySelf: "end",
            minWidth: 0,
            order: 1,
          })}
        >
          <Link
            className={css({ alignItems: "center", display: "flex", gap: "8px", minWidth: 0 })}
            params={{ facilityKey }}
            to="/facilities/$facilityKey/sensors/batch-registration"
          >
            <LuImport className={css({ flexShrink: 0, height: "14px", width: "auto" })} />
            <span className={TypographyStyle({ ellipsis: true, fontSize: "14px", lineHeight: 1 })}>
              {translate("%Add.2%")}
            </span>
          </Link>
          <Link
            className={css({ alignItems: "center", display: "flex", gap: "8px", minWidth: 0 })}
            params={{ facilityKey }}
            to="/facilities/$facilityKey/sensors/registration"
          >
            <LuPlus />
            <span className={TypographyStyle({ ellipsis: true, fontSize: "14px", lineHeight: 1 })}>
              {translate("%AddSensor.0%")}
            </span>
          </Link>
        </div>
      </div>

      <div className={css({ flexGrow: 1, marginTop: "8px", overflowX: "auto", width: "100%" })}>
        <SensorTable
          action={function (sensor) {
            return (
              <Link
                className={ButtonStyle({
                  appearance: "outlined",
                  borderRadius: "4px",
                  fontSize: "14px",
                  padding: "10px 16px",
                  color: "primary",
                })}
                params={{ facilityKey, sensorKey: sensor.sensorKey }}
                to="/facilities/$facilityKey/sensors/$sensorKey"
              >
                {translate("%Details.0%")}
              </Link>
            );
          }}
          className={css({ height: "100%" })}
          loading={loading}
          rows={pages.at(pageNumber)?.sensors}
        />
      </div>

      <div
        className={css({
          alignItems: "flex-start",
          display: "flex",
          marginTop: "8px",
          justifyContent: "space-between",
        })}
      >
        <PageNumberController
          loading={loading}
          next={pageNumber < pages.length || fetchNextPage !== undefined}
          onNext={function (value) {
            fetchNextPage?.().then(function () {
              setPageNumber(value);
            });
          }}
          onPrevious={function (value) {
            setPageNumber(value);
          }}
          value={pageNumber}
        />
        <PageSizeSelector
          className={css({ backgroundColor: "white" })}
          onChange={function (value) {
            setPageSize(value);
          }}
          options={[25, 50, 100]}
          value={pageSize}
        />
      </div>
    </main>
  );
}
